import { RichTextContent } from "../../../content/components";
import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import {
  StaticSiteDataContext,
  useIsClickToEditEnabled,
} from "../../../contexts/StaticSiteDataContext";
import { isEmptySlateDocument } from "../../../core/constants";
import { useRouterNavigation } from "../../../core/routing/routingHooks";
import {
  AttendantDetails,
  Configuration,
  DisplayMode,
  Page,
  Section,
  SectionTypes,
} from "../../../resources/modelTypes";
import EditButton from "../../../shared/EditButton";
import SitePreviewContext from "../../../website/components/SitePreviewContext";
import { sectionTypeToSwipePanelView } from "../../../website/components/online-invitation/mobile/SectionHeights";
import { selectors } from "../../cypressSelectors";
import { PageSlugs } from "../../types";
import { DisplayPhoto } from "../DisplayPhoto";
import { css, SerializedStyles } from "@emotion/react";
import { SmallHeadline, TinyHeadline } from "@minted/fancyclothes";
import { theme } from "@minted/minted-components";
import React, { memo, useContext, useCallback } from "react";

/**
 * Custom hook to handle section edit clicks using context
 */
const useEditClick = (page: Page) => {
  const { onSectionEditClick } = useContext(SitePreviewContext);
  const { isOnlineInvitationSite } = useContext(StaticSiteDataContext);

  return (sectionId: number) => {
    if (onSectionEditClick && isOnlineInvitationSite) {
      const panelView = sectionTypeToSwipePanelView(SectionTypes.Attendant);
      onSectionEditClick(sectionId, "section", panelView, page);
    }
  };
};

export type AttendantSection = Section & {
  sectionDetails: AttendantDetails;
};

type DisplayAttendantSectionProps = {
  configuration: Configuration;
  page: Page;
  sections: AttendantSection[];
  classes?: Record<string, SerializedStyles>;
};

type SingleAttendantProps = {
  configuration: Configuration;
  page: Page;
  section: AttendantSection;
  classes?: Record<string, SerializedStyles>;
};

const singleAttendantStyles = {
  alignment: (isHorizontal: boolean) => css`
    ${isHorizontal &&
    css`
      display: flex;
      justify-content: center;
    `}
  `,
  horizontalPhoto: css`
    width: 40%;
  `,
  spacing: css`
    margin-bottom: ${theme.spacing.x10};
  `,
  textContainer: (displayMode: DisplayMode) => css`
    display: flex;
    flex-direction: column;

    ${(displayMode === DisplayMode.Left || displayMode === DisplayMode.Right) &&
    css`
      margin-left: ${theme.spacing.x2};
      margin-right: ${theme.spacing.x2};
      width: 40%;
    `}

    ${displayMode === DisplayMode.Top &&
    css`
      margin-bottom: ${theme.spacing.x4};
    `}
    ${displayMode === DisplayMode.Bottom &&
    css`
      margin-top: ${theme.spacing.x4};
    `}
  `,
  wrapper: css`
    margin-top: ${theme.spacing.x2};
  `,
};

const SingleAttendant = ({
  configuration,
  page,
  section,
  classes,
}: SingleAttendantProps) => {
  const { isPreview } = useIsPreviewOrDemo();
  const navigateTo = useRouterNavigation();
  const handleEditClick = useEditClick(page);
  const { isWeddingSite } = useContext(StaticSiteDataContext);
  const isClickToEditEnabled = useIsClickToEditEnabled();
  const {
    focusedClickSection,
    hoveredSection,
    setFocusedClickSection,
    setHoveredSection,
    onSectionEditClickWithScroll,
  } = useContext(SitePreviewContext);

  const displayMode = section.sectionDetails.displayMode || DisplayMode.Top;
  const isHorizontal =
    displayMode === DisplayMode.Left || displayMode === DisplayMode.Right;

  const textContent = (
    <div
      css={singleAttendantStyles.textContainer(displayMode)}
      data-cy="attendant-text"
    >
      <div data-cy="subheading">
        <SmallHeadline>{section.sectionDetails.name}</SmallHeadline>
      </div>
      <div data-cy="body">
        <TinyHeadline>{section.sectionDetails.title}</TinyHeadline>
        {!isEmptySlateDocument(section.sectionDetails.content) && (
          <div>
            <RichTextContent value={section.sectionDetails.content} />
          </div>
        )}
      </div>
    </div>
  );

  const onAttendantEditClick = useCallback(() => {
    if (isPreview) {
      setFocusedClickSection({
        id: section.id,
        type: "section",
        isSolidBorder: true,
      });
      onSectionEditClickWithScroll(
        section.id,
        "section",
        SectionTypes.Attendant,
      );
      navigateTo(`pages.${PageSlugs.WeddingParty}`);
    }
  }, [
    isPreview,
    section.id,
    navigateTo,
    onSectionEditClickWithScroll,
    setFocusedClickSection,
  ]);

  return (
    <div
      css={singleAttendantStyles.spacing}
      data-cy={`${selectors.website_displaySection}_attendant`}
      id={`section-${section.id || "new"}`}
      key={`attendantGroup${section.id}`}
      onClick={
        isClickToEditEnabled
          ? () =>
              isWeddingSite
                ? onAttendantEditClick()
                : handleEditClick(section.id)
          : undefined
      }
      onMouseEnter={
        isClickToEditEnabled
          ? () => setHoveredSection({ id: section.id, type: "section" })
          : undefined
      }
      onMouseLeave={
        isClickToEditEnabled ? () => setHoveredSection(null) : undefined
      }
    >
      <EditButton sectionId={section.id} />
      <div
        css={css(
          singleAttendantStyles.alignment(isHorizontal),
          focusedClickSection?.id === section.id
            ? classes?.focusedSectionSolid
            : hoveredSection?.id === section.id
              ? classes?.dashedBorderStyle
              : undefined,
          isWeddingSite ? singleAttendantStyles.wrapper : undefined,
        )}
      >
        {(displayMode === DisplayMode.Right ||
          displayMode === DisplayMode.Bottom) &&
          textContent}
        {(section.photo || section.image) && (
          <div css={isHorizontal && singleAttendantStyles.horizontalPhoto}>
            <DisplayPhoto configuration={configuration} section={section} />
          </div>
        )}
        {(displayMode === DisplayMode.Left ||
          displayMode === DisplayMode.Top) &&
          textContent}
      </div>
    </div>
  );
};

export type GroupedAttendantsProps = {
  configuration: Configuration;
  page: Page;
  sections: AttendantSection[];
  classes?: Record<string, SerializedStyles>;
};

const groupedAttendantsStyles = {
  grid: css`
    display: grid;
    grid-template-columns: repeat(
      auto-fit,
      minmax(250px, 1fr)
    ); // Display 2 columns unless single attendant
    gap: ${theme.spacing.x4};
    justify-content: center;
    justify-items: center;
  `,
};

const GroupedAttendants = ({
  configuration,
  page,
  sections,
  classes,
}: GroupedAttendantsProps) => {
  const handleEditClick = useEditClick(page);

  return (
    <div
      css={groupedAttendantsStyles.grid}
      data-cy={`${selectors.website_displaySection}_attendantGroup`}
      id={`section-${sections[0].id || "new"}`}
      key={`attendantGroup${sections[0].id}`}
    >
      {sections.map((section) => (
        <div key={section.id} onClick={() => handleEditClick(section.id)}>
          <SingleAttendant
            configuration={configuration}
            page={page}
            section={section}
            classes={classes}
          />
        </div>
      ))}
    </div>
  );
};

const DisplayAttendantSections = ({
  configuration,
  page,
  sections,
  classes,
}: DisplayAttendantSectionProps) => {
  const { isMobile } = useContext(SitePreviewContext);

  if (configuration.pageLayout === "multi" && !isMobile) {
    return (
      <GroupedAttendants
        configuration={configuration}
        page={page}
        sections={sections}
        classes={classes}
      />
    );
  }

  return (
    <>
      {sections.map((section) => (
        <SingleAttendant
          configuration={configuration}
          key={section.id}
          page={page}
          section={section}
          classes={classes}
        />
      ))}
    </>
  );
};

const MemoizedDisplayAttendantSection = memo(DisplayAttendantSections);

export { MemoizedDisplayAttendantSection as DisplayAttendantSections };
